export default {
  en: {
    tabs: { overview: "Overview" },
    title: "Operations",
    subtitle: "Cost, spending limits, quotas, capacity, alerts, canaries and incidents.",
    cost: { byModel: "Cost by model ({{month}})", daily: "Daily cost (last 14 days)", forecast: "Forecast for the next {{days}} days: {{total}}" },
    budgets: {
      title: "Spending limits",
      scope: { tenant: "Organisation", team: "Team", project: "Project", agent: "Agent" },
      period: { day: "daily", month: "monthly" },
      metric: { cost: "cost", tokens: "tokens" },
      state: { ok: "Within the limit", soft: "Soft limit reached", exhausted: "Exhausted" },
      used: "{{used}} of {{limit}} used",
      detail: "Used {{used}} + reserved {{reserved}} of {{limit}} (remaining {{remaining}})",
      hard: "hard limit",
      soft: "soft limit only",
      add: "Add spending limit",
      editTitle: "Edit spending limit",
      edit: "Edit spending limit {{name}}",
      delete: "Delete spending limit {{name}}",
      deleteConfirm: "Delete this spending limit? Spending is no longer checked against it.",
      saved: "Spending limit saved",
      save: "Save",
      cancel: "Cancel",
      scopeLabel: "Scope",
      scopeValue: "Applies to",
      scopeValueHelp: "Team, project or agent name; * for all",
      periodLabel: "Period",
      metricLabel: "Metric",
      limit: "Limit",
      softPct: "Soft limit (%)",
      hardLabel: "Hard limit: deny requests when exhausted",
    },
    issues: {
      scope_value_invalid: "Use a name of letters, digits, '_', '.', ':', '@', '/' or '-', or * for all.",
      limit_invalid: "Limit must be a number above 0 (at most 6 decimals).",
      soft_pct_invalid: "Soft limit must be above 0 and at most 100.",
    },
    alerts: {
      title: "Alerts",
      metering: "Spending limits and anomalies",
      platform: "Platform",
      none: "No spending limit alerts",
      since: "since {{at}}",
      kind: { budget_soft_limit: "Spending limit nearly reached", budget_exhausted: "Spending limit reached", anomaly: "Unusual use" },
      anomalyModel: "{{model}} handled much more text today than usual ({{today}} units against about {{usual}} a day)",
      anomalyCost: "Spending today is much higher than usual ({{today}} against about {{usual}} a day)",
      platformNone: "The platform is running normally. Nothing needs your attention.",
    },
    quotas: { title: "Quotas", empty: "No limits are set for this organisation yet. Your platform team sets them when the platform runs on Kubernetes.", resource: "Resource", usage: "Usage", pct: "Percent", res: { active_runs: "Active runs", gpu_seconds_day: "GPU seconds per day", storage_gb: "Storage (GB)" } },
    capacity: { title: "Capacity (30-day forecast)", empty: "No forecast yet. It appears after a few days of use.", pool: "Node pool", utilisation: "Peak utilisation", action: "Recommendation" },
    canary: { title: "Canary releases", empty: "No gradual roll-out is in progress. Platform updates appear here while they roll out.", analysis: "Analysis: {{state}}", step: "Step {{step}} of {{steps}} · traffic {{weight}}" },
    incidents: {
      title: "Incidents",
      what: "What happened",
      severity: "How serious",
      status: "Status",
      opened: "Started",
      commander: "Who stopped it",
      reason: "Reason: {{reason}}",
      scope: { global: "Every agent was stopped", tenant: "All of the organisation's agents were stopped", agent: "{{agent}} was stopped" },
      sev: { sev1: "Critical", sev2: "High", sev3: "Low" },
      showResolved: "Show resolved",
      none: "No incidents so far.",
      noneOpen: "No open incidents. Turn on \"Show resolved\" to see earlier ones.",
    },
  },
  ar: {
    tabs: { overview: "نظرة عامة" },
    title: "العمليات",
    subtitle: "التكلفة وحدود الإنفاق والحصص والسعة والتنبيهات والإصدارات التجريبية والحوادث.",
    cost: { byModel: "التكلفة حسب النموذج ({{month}})", daily: "التكلفة اليومية (آخر 14 يومًا)", forecast: "التوقع للأيام الـ {{days}} القادمة: {{total}}" },
    budgets: {
      title: "حدود الإنفاق",
      scope: { tenant: "الجهة", team: "الفريق", project: "المشروع", agent: "الوكيل" },
      period: { day: "يومية", month: "شهرية" },
      metric: { cost: "التكلفة", tokens: "الرموز" },
      state: { ok: "ضمن حد الإنفاق", soft: "بلغ الحد التحذيري", exhausted: "مستنفدة" },
      used: "استُخدم {{used}} من {{limit}}",
      detail: "مستخدم {{used}} + محجوز {{reserved}} من {{limit}} (المتبقي {{remaining}})",
      hard: "حد صارم",
      soft: "حد تحذيري فقط",
      add: "إضافة حد إنفاق",
      editTitle: "تعديل حد الإنفاق",
      edit: "تعديل حد الإنفاق {{name}}",
      delete: "حذف حد الإنفاق {{name}}",
      deleteConfirm: "حذف هذا الحد؟ لن يُتحقق من الإنفاق مقابله بعد الآن.",
      saved: "تم حفظ حد الإنفاق",
      save: "حفظ",
      cancel: "إلغاء",
      scopeLabel: "النطاق",
      scopeValue: "ينطبق على",
      scopeValueHelp: "اسم الفريق أو المشروع أو الوكيل؛ * للجميع",
      periodLabel: "الفترة",
      metricLabel: "المقياس",
      limit: "الحد",
      softPct: "الحد التحذيري (٪)",
      hardLabel: "حد صارم: رفض الطلبات عند الاستنفاد",
    },
    issues: {
      scope_value_invalid: "استخدم اسمًا من أحرف لاتينية وأرقام و'_' أو '.' أو ':' أو '@' أو '/' أو '-'، أو * للجميع.",
      limit_invalid: "يجب أن يكون الحد عددًا أكبر من 0 (حتى 6 خانات عشرية).",
      soft_pct_invalid: "يجب أن يكون الحد التحذيري أكبر من 0 ولا يتجاوز 100.",
    },
    alerts: {
      title: "التنبيهات",
      metering: "حدود الإنفاق والحالات الشاذة",
      platform: "المنصة",
      none: "لا توجد تنبيهات حدود الإنفاق",
      since: "منذ {{at}}",
      kind: { budget_soft_limit: "الحد التحذيري لحد الإنفاق", budget_exhausted: "استنفاد حد الإنفاق", anomaly: "استخدام غير معتاد" },
      anomalyModel: "عالج {{model}} اليوم نصًا أكثر من المعتاد بكثير ({{today}} وحدة مقابل نحو {{usual}} يوميًا)",
      anomalyCost: "إنفاق اليوم أعلى من المعتاد بكثير ({{today}} مقابل نحو {{usual}} يوميًا)",
      platformNone: "المنصة تعمل بشكل طبيعي. لا شيء يحتاج إلى انتباهك.",
    },
    quotas: { title: "الحصص", empty: "لم تُضبط حدود لهذه الجهة بعد. يضبطها فريق المنصة عند تشغيلها على Kubernetes.", resource: "المورد", usage: "الاستخدام", pct: "النسبة", res: { active_runs: "التشغيلات النشطة", gpu_seconds_day: "ثواني المعالج الرسومي يوميًا", storage_gb: "التخزين (غيغابايت)" } },
    capacity: { title: "السعة (توقع 30 يومًا)", empty: "لا يوجد توقع بعد. يظهر بعد بضعة أيام من الاستخدام.", pool: "مجموعة العقد", utilisation: "ذروة الاستخدام", action: "التوصية" },
    canary: { title: "الإصدارات التجريبية", empty: "لا يوجد طرح تدريجي الآن. تظهر تحديثات المنصة هنا أثناء طرحها.", analysis: "التحليل: {{state}}", step: "الخطوة {{step}} من {{steps}} · الحركة {{weight}}" },
    incidents: {
      title: "الحوادث",
      what: "ما الذي حدث",
      severity: "مدى الخطورة",
      status: "الحالة",
      opened: "البداية",
      commander: "من أوقفه",
      reason: "السبب: {{reason}}",
      scope: { global: "أُوقف جميع الوكلاء", tenant: "أُوقف جميع وكلاء الجهة", agent: "أُوقف الوكيل {{agent}}" },
      sev: { sev1: "حرجة", sev2: "عالية", sev3: "منخفضة" },
      showResolved: "عرض المحلولة",
      none: "لا توجد حوادث حتى الآن.",
      noneOpen: "لا توجد حوادث مفتوحة. فعّل \"عرض المحلولة\" لرؤية السابقة.",
    },
  },
};
